import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Settings } from '../types';
import {
  DEFAULT_SETTINGS,
  USERNAME_EDIT_COOLDOWN,
  USERNAME_EDIT_COST,
  getDefaultBackground,
} from '../data/constants';
import { MAX_LEVEL, MAX_PRESTIGE, getXPNeeded } from '../data/levels';
import { getMilestoneForDay, type MilestoneReward } from '../data/milestones';
import { calculateRoleXP } from '../data/roleSystem';
import { getActiveBuffs } from '../data/eventBuffsData';

// Import shared helpers from store slices
import { getIsMobile, getValidBackgroundForDevice } from './slices/visualSlice';

// Import for background sync with retry
import { saveCompletedPomodoro } from '../lib/userSyncAuth';
import { supabase } from '../lib/supabase';
import { withRetryAsync } from '../lib/retryUtils';
import { queuePomodoroOperation } from '../lib/syncQueue';

interface SettingsStore extends Settings {
  // Timer actions
  setPomodoroDuration: (minutes: number) => void;
  setShortBreakDuration: (minutes: number) => void;
  setLongBreakDuration: (minutes: number) => void;
  setPomodorosBeforeLongBreak: (count: number) => void;
  setAutoStartBreaks: (enabled: boolean) => void;
  setAutoStartPomodoros: (enabled: boolean) => void;

  // Audio actions
  setSoundEnabled: (enabled: boolean) => void;
  setVolume: (volume: number) => void;
  setMusicVolume: (volume: number) => void;
  setAmbientVolume: (soundId: string, volume: number) => void;
  resetAmbientVolumes: () => void;

  // Visual actions
  setBackground: (background: string) => void;
  backgroundMobile: string;
  backgroundDesktop: string;
  setAutoHideUI: (enabled: boolean) => void;
  autoHideUI: boolean;
  setPlaylist: (playlist: 'lofi' | 'synthwave') => void;
  snowEnabled: boolean;
  toggleSnow: () => void;

  // Level system actions
  addXP: (minutes: number) => void;
  addDailyGiftXP: (xpAmount: number, skipSync?: boolean) => void;
  setUsername: (username: string, forceWithXP?: boolean) => void;
  setLevelPath: (path: 'elf' | 'human') => void;
  setLevelSystemEnabled: (enabled: boolean) => void;
  resetProgress: () => void;
  prestige: () => void;

  // Milestone system actions
  unlockMilestoneReward: (milestone: MilestoneReward) => void;
  simulateUniqueDay: () => void; // Dev-only function to test milestones

  // Login tracking
  trackLogin: () => { isNewDay: boolean; currentDay: number; giftAlreadyClaimed: boolean };
  markDailyGiftClaimed: () => void;

  // Sync state
  settingsSyncComplete: boolean;
  setSettingsSyncComplete: (complete: boolean) => void;

  // Computed
  canEditUsername: () => boolean;
  getXPCost: () => number;

  // Onboarding tour
  hasSeenTour: boolean;
  setHasSeenTour: (value: boolean) => void;
  isTourActive: boolean;
  setIsTourActive: (value: boolean) => void;

  // Global Modal State
  isSettingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  isUserStatsOpen: boolean;
  setUserStatsOpen: (open: boolean) => void;
  isShortcutModalOpen: boolean;
  setShortcutModalOpen: (open: boolean) => void;
  isMobilePlayerExpanded: boolean;
  setIsMobilePlayerExpanded: (expanded: boolean) => void;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      // Initial state from defaults with device-aware background
      ...DEFAULT_SETTINGS,
      background: getValidBackgroundForDevice(DEFAULT_SETTINGS.background, getIsMobile()),
      backgroundMobile: getDefaultBackground(true),
      backgroundDesktop: getDefaultBackground(false),

      // Sync state (not persisted - always starts false)
      settingsSyncComplete: false,
      setSettingsSyncComplete: (complete) => set({ settingsSyncComplete: complete }),

      // Global Modal State
      isSettingsOpen: false,
      setSettingsOpen: (open) => set({ isSettingsOpen: open }),
      isUserStatsOpen: false,
      setUserStatsOpen: (open) => set({ isUserStatsOpen: open }),
      isShortcutModalOpen: false,
      setShortcutModalOpen: (open) => set({ isShortcutModalOpen: open }),
      isMobilePlayerExpanded: false,
      setIsMobilePlayerExpanded: (expanded) => set({ isMobilePlayerExpanded: expanded }),

      // Timer actions
      setPomodoroDuration: (minutes) =>
        set((state) => ({
          timers: { ...state.timers, pomodoro: minutes },
        })),

      setShortBreakDuration: (minutes) =>
        set((state) => ({
          timers: { ...state.timers, shortBreak: minutes },
        })),

      setLongBreakDuration: (minutes) =>
        set((state) => ({
          timers: { ...state.timers, longBreak: minutes },
        })),

      setPomodorosBeforeLongBreak: (count) => set({ pomodorosBeforeLongBreak: count }),

      setAutoStartBreaks: (enabled) => set({ autoStartBreaks: enabled }),

      setAutoStartPomodoros: (enabled) => set({ autoStartPomodoros: enabled }),

      // Audio actions
      setSoundEnabled: (enabled) => set({ soundEnabled: enabled }),

      setVolume: (volume) => set({ volume }),

      setMusicVolume: (volume) => set({ musicVolume: volume }),

      setAmbientVolume: (soundId, volume) =>
        set((state) => ({
          ambientVolumes: { ...state.ambientVolumes, [soundId]: volume },
        })),

      resetAmbientVolumes: () => set({ ambientVolumes: {} }),

      // Visual actions
      setBackground: (background) => {
        const isMobile = getIsMobile();
        const validBackground = getValidBackgroundForDevice(background, isMobile);

        set((state) => ({
          background: validBackground,
          // Update the specific preference based on current device type
          backgroundMobile: isMobile ? validBackground : state.backgroundMobile,
          backgroundDesktop: !isMobile ? validBackground : state.backgroundDesktop,
        }));
      },
      setAutoHideUI: (enabled) => set({ autoHideUI: enabled }),
      setPlaylist: (playlist) => set({ playlist }),

      // Effects
      snowEnabled: true,
      toggleSnow: () => set((state) => ({ snowEnabled: !state.snowEnabled })),

      // Level system actions
      addXP: (minutes) => {
        const state = get();

        if (import.meta.env.DEV)
          console.log(
            '[XP] addXP called with minutes:',
            minutes,
            'Current level:',
            state.level,
            'Current XP:',
            state.xp,
            'Role:',
            state.levelPath
          );

        // Check if pomodoro boost is active and not expired
        let boostMultiplier = 1;
        let boostStillActive = state.pomodoroBoostActive;

        if (state.pomodoroBoostActive && state.pomodoroBoostExpiresAt) {
          if (Date.now() > state.pomodoroBoostExpiresAt) {
            // Boost has expired
            boostStillActive = false;
            if (import.meta.env.DEV) console.log('[XP] Pomodoro boost expired');
          } else {
            // Boost is still active - use the actual multiplier from state
            boostMultiplier = state.pomodoroBoostMultiplier || 1.25;
            const boostPercent = Math.round((boostMultiplier - 1) * 100);
            if (import.meta.env.DEV) console.log(`[XP] Applying +${boostPercent}% XP boost!`);
          }
        }

        // Role-based XP calculation using role system
        const roleResult = calculateRoleXP(state.levelPath, minutes, {
          consecutiveDays: state.consecutiveLoginDays,
          prestigeLevel: state.prestigeLevel,
          consecutiveCrits: state.consecutiveCriticals,
        });

        const baseXP = roleResult.xpGained;
        const criticalSuccess = roleResult.criticalSuccess;

        // Log role bonuses
        if (roleResult.bonuses.length > 0) {
          if (import.meta.env.DEV)
            console.log('[XP] Role buffs applied:', roleResult.bonuses.join(', '));
        }

        // Get all active event buffs (date-based)
        const activeEventBuffs = getActiveBuffs(new Date(), state.weekendDays);

        // Filter role-specific buffs
        const roleFilteredBuffs = activeEventBuffs.filter((buff) => {
          if (buff.description.includes('(Elf only)')) {
            return state.levelPath === 'elf';
          }
          if (buff.description.includes('(Human only)')) {
            return state.levelPath === 'human';
          }
          return true; // Buff applies to all roles
        });

        // Calculate event buff multiplier from role-filtered buffs (stacked ADDITIVELY)
        // Example: 1.25 (25%) + 1.30 (30%) = 1.55 (55%), NOT 1.625
        const eventBuffMultiplier = roleFilteredBuffs.reduce((total, buff) => {
          return total + (buff.xpMultiplier - 1);
        }, 1);

        // Calculate flat XP bonus from active buffs
        // ANTI-ABUSE: Only award flat bonus if session is at least 15 minutes
        let flatXPBonus = 0;
        if (minutes >= 15) {
          flatXPBonus = roleFilteredBuffs.reduce((total, buff) => {
            return total + (buff.flatXPBonus || 0);
          }, 0);
        } else if (roleFilteredBuffs.some((b) => b.flatXPBonus && b.flatXPBonus > 0)) {
          if (import.meta.env.DEV)
            console.log('[XP] Session too short (min 15m) - Flat XP bonus skipped');
        }

        if (eventBuffMultiplier > 1 && roleFilteredBuffs.length > 0) {
          if (import.meta.env.DEV) {
            console.log(
              '[XP] Event buffs active:',
              roleFilteredBuffs.map((b) => `${b.title} (x${b.xpMultiplier})`).join(', ')
            );
            console.log('[XP] Stacked event buff multiplier:', eventBuffMultiplier.toFixed(3));
          }
        }

        if (flatXPBonus > 0) {
          if (import.meta.env.DEV)
            console.log(`[XP] Flat XP bonus from event buffs: +${flatXPBonus}`);
        }

        // Calculate final XP with all multipliers stacked
        // Order: base XP → daily boost → event buffs (multiplier) → flat bonus
        const xpWithBoost = baseXP * boostMultiplier;
        const xpWithEventBuffs = xpWithBoost * eventBuffMultiplier;
        const xpGained = Math.floor(xpWithEventBuffs) + flatXPBonus;
        let newXP = state.xp + xpGained;
        let newLevel = state.level;
        let newPrestigeLevel = state.prestigeLevel;

        if (import.meta.env.DEV)
          console.log(
            '[XP] XP gained:',
            xpGained,
            'New XP total:',
            newXP,
            'XP needed for next level:',
            getXPNeeded(newLevel)
          );

        // Check for level ups
        while (newLevel < MAX_LEVEL && newXP >= getXPNeeded(newLevel)) {
          newXP -= getXPNeeded(newLevel);
          newLevel++;
          if (import.meta.env.DEV)
            console.log('[XP] 🎉 LEVEL UP! New level:', newLevel, 'Remaining XP:', newXP);
        }

        // Check for prestige at max level (only if not at max prestige)
        if (
          newLevel >= MAX_LEVEL &&
          newXP >= getXPNeeded(newLevel) &&
          newPrestigeLevel < MAX_PRESTIGE
        ) {
          // Consume XP for level 100 before prestiging
          newXP -= getXPNeeded(newLevel);
          newPrestigeLevel++;
          newLevel = 1;
          if (import.meta.env.DEV)
            console.log('[XP] ⭐ PRESTIGE! New prestige level:', newPrestigeLevel);

          // Continue leveling with remaining XP after prestige
          while (newLevel < MAX_LEVEL && newXP >= getXPNeeded(newLevel)) {
            newXP -= getXPNeeded(newLevel);
            newLevel++;
            if (import.meta.env.DEV)
              console.log('[XP] 🎉 LEVEL UP! New level:', newLevel, 'Remaining XP:', newXP);
          }
        } else if (newLevel >= MAX_LEVEL && newPrestigeLevel >= MAX_PRESTIGE) {
          // At max prestige, stay at level 100, XP keeps accumulating
          newLevel = MAX_LEVEL;
          if (import.meta.env.DEV)
            console.log('[XP] 🏆 MAX PRESTIGE REACHED! XP continues to accumulate.');
        }

        // Check for daily milestone progress
        const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        let newTotalUniqueDays = state.totalUniqueDays;

        if (state.lastPomodoroDate !== today) {
          // This is the first Pomodoro of a new day
          newTotalUniqueDays = state.totalUniqueDays + 1;

          // Check if this new day count matches a milestone
          const milestone = getMilestoneForDay(newTotalUniqueDays);
          if (milestone) {
            // Unlock the milestone reward
            get().unlockMilestoneReward(milestone);
          }
        }

        // Update role-specific stats
        let newConsecutiveCrits = state.consecutiveCriticals;
        let newTodayPomodoros = state.todayPomodoros;
        let newComebackActive = state.comebackActive;
        let newComebackPomodoros = state.comebackPomodoros;

        // Reset today counter if new day
        if (state.lastPomodoroDate !== today) {
          newTodayPomodoros = 0;
        }
        newTodayPomodoros++;

        // Update consecutive criticals for humans
        if (state.levelPath === 'human') {
          if (criticalSuccess) {
            newConsecutiveCrits = Math.max(0, newConsecutiveCrits) + 1;
            if (import.meta.env.DEV) console.log('[XP] Consecutive crits:', newConsecutiveCrits);
          } else {
            newConsecutiveCrits = Math.min(0, newConsecutiveCrits) - 1;
            if (import.meta.env.DEV)
              console.log('[XP] Consecutive fails:', Math.abs(newConsecutiveCrits));
          }

          // Comeback buff: use and decrement
          if (newComebackActive && newComebackPomodoros > 0) {
            newComebackPomodoros--;
            if (newComebackPomodoros === 0) {
              newComebackActive = false;
              if (import.meta.env.DEV) console.log('[XP] Comeback buff expired');
            }
          }
        }

        // Update local store first (optimistic update for instant UI feedback)
        if (import.meta.env.DEV)
          console.log(
            '[XP] Updating state - Old level:',
            state.level,
            '→ New level:',
            newLevel,
            '| Old XP:',
            state.xp,
            '→ New XP:',
            newXP
          );
        set({
          totalXP: state.totalXP + xpGained, // Track total XP earned
          xp: newXP, // Remaining XP for progress display
          level: newLevel,
          prestigeLevel: newPrestigeLevel,
          totalPomodoros: state.totalPomodoros + 1,
          totalStudyMinutes: state.totalStudyMinutes + minutes,
          totalUniqueDays: newTotalUniqueDays,
          lastPomodoroDate: today,
          pomodoroBoostActive: boostStillActive,
          pomodoroBoostExpiresAt: boostStillActive ? state.pomodoroBoostExpiresAt : null,
          consecutiveCriticals: newConsecutiveCrits,
          todayPomodoros: newTodayPomodoros,
          comebackActive: newComebackActive,
          comebackPomodoros: newComebackPomodoros,
        });
        if (import.meta.env.DEV) console.log('[XP] State updated successfully');

        // Sync to database in background with retry logic
        // This ensures XP persists across page refreshes
        (async () => {
          try {
            // Get userId and discordId from store state
            const { userId, discordId } = get();

            if (!userId || !discordId) {
              console.warn('[addXP] No user ID or Discord ID - XP saved locally only');
              return;
            }

            // Save pomodoro to database with retry logic
            // Retries up to 3 times with exponential backoff, reports to Sentry on failure
            const sessionStartedAt = Date.now() - minutes * 60 * 1000; // Estimate session start

            const result = await withRetryAsync(
              () =>
                saveCompletedPomodoro(userId, {
                  duration_minutes: minutes,
                  xp_earned: xpGained,
                  critical_success: criticalSuccess,
                }),
              {
                context: {
                  operation: 'saveCompletedPomodoro',
                  userId,
                  minutes,
                  xpGained,
                },
              }
            );

            if (result) {
              if (import.meta.env.DEV) console.log('[addXP] ✓ XP synced to database');
            } else {
              // All retries failed - queue for later
              if (import.meta.env.DEV) console.log('[addXP] Queuing pomodoro for later sync');
              queuePomodoroOperation(
                userId,
                {
                  duration_minutes: minutes,
                  xp_earned: xpGained,
                  critical_success: criticalSuccess,
                },
                sessionStartedAt
              );
            }
          } catch (error) {
            console.error('[addXP] Unexpected error during sync:', error);
          }
        })();
      },

      addDailyGiftXP: async (xpAmount, skipSync = false) => {
        if (skipSync) {
          // For non-synced testing/local only
          const state = get();
          let newXP = state.xp + xpAmount;
          let newLevel = state.level;
          while (newLevel < MAX_LEVEL && newXP >= getXPNeeded(newLevel)) {
            newXP -= getXPNeeded(newLevel);
            newLevel++;
          }
          set({ xp: newXP, level: newLevel, lastDailyGiftDate: new Date().toISOString() });
          return;
        }

        // Check if gift already claimed today (optimistic check) to avoid unnecessary calls
        const today = new Date().toDateString();
        const lastClaim = get().lastDailyGiftDate
          ? new Date(get().lastDailyGiftDate!).toDateString()
          : null;

        if (lastClaim === today) {
          console.log('[addDailyGiftXP] Already claimed today (local check)');
          return;
        }

        try {
          // Call Server-Side RPC
          const { data: response, error } = await supabase.rpc('claim_daily_gift', {
            p_user_id: get().userId!,
            p_discord_id: get().discordId || null,
          });

          if (error) throw error;

          if (response && response.success) {
            const earnedXP = response.xp_earned;
            const currentXP = get().xp || 0;

            // Calculate new level locally for immediate UI update OR fetch fresh profile
            // For smoother UI, we'll do a quick local calc + update lastDailyGiftDate
            //Ideally, we should refetch the user profile to be perfectly in sync with server's level up logic

            let newXP = currentXP + earnedXP;
            let newLevel = get().level;

            // Simple local level simulation to match server
            while (newLevel < MAX_LEVEL && newXP >= getXPNeeded(newLevel)) {
              newXP -= getXPNeeded(newLevel);
              newLevel++;
            }

            set({
              xp: newXP,
              level: newLevel,
              lastDailyGiftDate: new Date().toISOString(),
            });

            if (import.meta.env.DEV) console.log(`[addDailyGiftXP] Claimed ${earnedXP} XP via RPC`);
          } else {
            console.log(`[addDailyGiftXP] Claim failed: ${response?.message}`);
          }
        } catch (err) {
          console.error('[addDailyGiftXP] RPC Error:', err);
        }
      },

      setUsername: (username, forceWithXP = false) => {
        const state = get();

        if (forceWithXP) {
          // Spend XP to change username early
          if (state.xp >= USERNAME_EDIT_COST) {
            set({
              username,
              xp: state.xp - USERNAME_EDIT_COST,
              lastUsernameChange: Date.now(),
            });
          }
        } else {
          // Normal username change (cooldown must have passed)
          set({
            username,
            lastUsernameChange: Date.now(),
          });
        }
      },

      setLevelPath: (path) => set({ levelPath: path }),

      setLevelSystemEnabled: (enabled) => set({ levelSystemEnabled: enabled }),

      resetProgress: () =>
        set({
          xp: 0,
          level: 1,
          prestigeLevel: 0,
          totalPomodoros: 0,
          totalStudyMinutes: 0,
        }),

      prestige: () => {
        const state = get();
        set({
          level: 1,
          xp: 0,
          prestigeLevel: state.prestigeLevel + 1,
        });
      },

      // Milestone system actions
      unlockMilestoneReward: (milestone) => {
        if (import.meta.env.DEV) {
          console.log(`🎉 Milestone Unlocked: ${milestone.title}`);
          console.log(`📝 ${milestone.description}`);
          console.log(`🎁 Reward: ${milestone.rewardType} - ${milestone.unlockId}`);
        }

        // TODO: In the future, this will unlock actual backgrounds, themes, or badges
        // For now, just logging to console
        // Example future implementation:
        // - Add to unlockedBackgrounds array
        // - Add to unlockedThemes array
        // - Show a notification toast
      },

      simulateUniqueDay: () => {
        const state = get();
        const newTotalUniqueDays = state.totalUniqueDays + 1;

        // Check if this new day count matches a milestone
        const milestone = getMilestoneForDay(newTotalUniqueDays);
        if (milestone) {
          get().unlockMilestoneReward(milestone);
        }

        // Update state with new unique day count and a fake "yesterday" date
        // so the next real Pomodoro will count as today
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        set({
          totalUniqueDays: newTotalUniqueDays,
          lastPomodoroDate: yesterdayStr,
        });
      },

      trackLogin: () => {
        const state = get();
        const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

        // First time user (no last login date)
        if (!state.lastLoginDate) {
          if (import.meta.env.DEV) console.log('[trackLogin] First time user - setting day 1');
          set({
            lastLoginDate: today,
            consecutiveLoginDays: 1,
            totalLoginDays: 1,
            firstLoginDate: today,
          });
          return {
            isNewDay: true,
            currentDay: 1,
            giftAlreadyClaimed: false,
          };
        }

        // Check if this is a new day
        if (state.lastLoginDate === today) {
          // Same day, check if gift was already claimed today
          const giftAlreadyClaimed = state.lastDailyGiftDate === today;
          if (import.meta.env.DEV)
            console.log(
              '[trackLogin] Same day - lastDailyGiftDate:',
              state.lastDailyGiftDate,
              'today:',
              today,
              'claimed:',
              giftAlreadyClaimed
            );
          return {
            isNewDay: false,
            currentDay: state.consecutiveLoginDays,
            giftAlreadyClaimed,
          };
        }

        // It's a new day!
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        // Check if login is consecutive
        const isConsecutive = state.lastLoginDate === yesterdayStr;
        const newConsecutiveDays = isConsecutive ? state.consecutiveLoginDays + 1 : 1;
        const newTotalLoginDays = state.totalLoginDays + 1;

        set({
          lastLoginDate: today,
          consecutiveLoginDays: newConsecutiveDays,
          totalLoginDays: newTotalLoginDays,
        });

        // Gift hasn't been claimed today (it's a new day)
        if (import.meta.env.DEV)
          console.log(
            '[trackLogin] New day - consecutiveLoginDays:',
            newConsecutiveDays,
            'giftAlreadyClaimed: false'
          );
        return {
          isNewDay: true,
          currentDay: newConsecutiveDays,
          giftAlreadyClaimed: false,
        };
      },

      markDailyGiftClaimed: () => {
        const today = new Date().toISOString().split('T')[0];
        set({ lastDailyGiftDate: today });
        if (import.meta.env.DEV) console.log('[DailyGift] Marked daily gift as claimed for', today);
      },

      // Computed
      canEditUsername: () => {
        const state = get();
        if (!state.lastUsernameChange) return true;
        return Date.now() - state.lastUsernameChange >= USERNAME_EDIT_COOLDOWN;
      },

      getXPCost: () => USERNAME_EDIT_COST,

      // Onboarding tour
      setHasSeenTour: (value) => set({ hasSeenTour: value }),
      isTourActive: false,
      setIsTourActive: (value) => set({ isTourActive: value }),
    }),
    {
      name: 'pomodoroSettings',
      merge: (persistedState, currentState) => {
        // Deep merge persisted state with defaults, ensuring new properties get their defaults
        const merged = { ...currentState, ...(persistedState as object) };

        // Ensure hasSeenTour defaults to false if not present in persisted state
        if (typeof (persistedState as Record<string, unknown>)?.hasSeenTour === 'undefined') {
          (merged as Record<string, unknown>).hasSeenTour = false;
        }

        return merged as typeof currentState;
      },
      onRehydrateStorage: () => (state) => {
        // After loading from localStorage, validate background compatibility
        if (state) {
          const isMobile = getIsMobile();

          // Switch to the correct background preference for this device
          // If preference exists, use it. Otherwise use current background if valid, or default.
          let targetBackground = isMobile ? state.backgroundMobile : state.backgroundDesktop;

          // Fallback if specific preference is missing (legacy state)
          if (!targetBackground) {
            targetBackground = state.background;
          }

          const validBackground = getValidBackgroundForDevice(targetBackground, isMobile);

          // Apply the validated background
          state.background = validBackground;

          // Ensure preferences are populated if they were empty (migration from old state)
          if (!state.backgroundMobile) state.backgroundMobile = getDefaultBackground(true);
          if (!state.backgroundDesktop) state.backgroundDesktop = getDefaultBackground(false);
        }
      },
    }
  )
);
